import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCart, removeFromCart, updateQuantity, clearCart, getCartTotal } from '../utils/cartUtils';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setCart(getCart());
  }, []);

  const handleRemove = (productId) => {
    const updatedCart = removeFromCart(productId);
    setCart(updatedCart);
    toast.success('Item removed from cart');
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    const updatedCart = updateQuantity(productId, newQuantity);
    setCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleClearCart = () => {
    clearCart();
    setCart([]);
    toast.success('Cart cleared');
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const total = getCartTotal();
  const shipping = total > 50 ? 0 : 5.99;
  const grandTotal = total + shipping;

  if (cart.length === 0) {
    return (
      <div className="empty-cart-container">
        <Card className="empty-cart-card">
          <ShoppingBag size={64} className="empty-cart-icon" />
          <h2 className="empty-cart-title">Your Cart is Empty</h2>
          <p className="empty-cart-text">Add some natural wellness products to get started!</p>
          <Button asChild size="lg" className="mt-6">
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <Button variant="ghost" onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={18} className="mr-2" /> Continue Shopping
          </Button>
          <h1 className="cart-title">Shopping Cart</h1>
          <Button variant="outline" onClick={handleClearCart} className="clear-cart-btn">
            Clear Cart
          </Button>
        </div>

        <div className="cart-layout">
          {/* Cart Items */}
          <div className="cart-items-section">
            {cart.map(item => (
              <Card key={item.id} className="cart-item">
                <div className="cart-item-image-wrapper">
                  <img src={item.image} alt={item.name} className="cart-item-image" />
                </div>
                <div className="cart-item-details">
                  <Link to={`/product/${item.id}`} className="cart-item-name">
                    {item.name}
                  </Link>
                  <p className="cart-item-category">{item.category}</p>
                  <p className="cart-item-price">${item.price.toFixed(2)}</p>
                </div>
                <div className="cart-item-actions">
                  <div className="cart-quantity-controls">
                    <button
                      onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                      className="cart-quantity-btn"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="cart-quantity-display">{item.quantity}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      className="cart-quantity-btn"
                      aria-label="Increase quantity"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <p className="cart-item-total">${(item.price * item.quantity).toFixed(2)}</p>
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="cart-remove-btn"
                    aria-label="Remove item"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {/* Cart Summary */}
          <div className="cart-summary-section">
            <Card className="cart-summary-card">
              <h2 className="cart-summary-title">Order Summary</h2>
              <div className="cart-summary-row">
                <span>Subtotal:</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <div className="cart-summary-row">
                <span>Shipping:</span>
                <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
              </div>
              {total < 50 && (
                <p className="free-shipping-notice">
                  Add ${(50 - total).toFixed(2)} more for free shipping!
                </p>
              )}
              <div className="cart-summary-divider"></div>
              <div className="cart-summary-total">
                <span>Total:</span>
                <span>${grandTotal.toFixed(2)}</span>
              </div>
              <Button size="lg" onClick={handleCheckout} className="checkout-button">
                Proceed to Checkout
              </Button>
              <Button asChild variant="outline" size="lg" className="mt-3 w-full">
                <Link to="/shop">Continue Shopping</Link>
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
